import { ContentCount, ContentStrategy } from 'types'
import { ContentTypeCounts, DEFAULT_CONTENT_TYPE_COUNTS } from 'utils/brandPlans/contentCategories'

const sumCounts = (items: ContentCount[] | undefined, filter?: (item: ContentCount) => boolean): number =>
  (items ?? []).filter(filter ?? (() => true)).reduce((sum, item) => sum + (item.count ?? 0), 0)

const sumByContentType = (items: ContentCount[] | undefined, contentType: string): number =>
  sumCounts(items, (item) => item.content_type === contentType)

export function deriveInitialCountsFromStrategy(config: ContentStrategy | null): ContentTypeCounts {
  if (!config) return { ...DEFAULT_CONTENT_TYPE_COUNTS }

  const crosspost = config.crosspost_contents
  const untypedCrosspost = sumCounts(crosspost, (item) => item.type === 'post' && !item.content_type)

  return {
    still_image_post: sumByContentType(crosspost, 'still_image') + untypedCrosspost,
    carousel_post: sumByContentType(crosspost, 'carousel'),
    feed_video_post: sumByContentType(crosspost, 'video'),
    short_form_video: sumCounts(config.video_crosspost_contents, (item) => item.content_type !== 'avatar_video'),
    avatar_video: sumByContentType(config.video_crosspost_contents, 'avatar_video'),
    stories: sumCounts(config.instagram_contents, (item) => item.type === 'story'),
    blog_post: sumCounts(config.seo_contents),
    newsletter: sumCounts(config.email_contents),
  }
}

export function buildContentsFromCounts(counts: ContentTypeCounts) {
  const crosspostContents: ContentCount[] = []
  const videoContents: ContentCount[] = []
  const instagramContents: ContentCount[] = []
  const seoContents: ContentCount[] = []
  const emailContents: ContentCount[] = []

  if (counts.still_image_post > 0) {
    crosspostContents.push({ type: 'post', count: counts.still_image_post, content_type: 'still_image' })
  }
  if (counts.carousel_post > 0) {
    crosspostContents.push({ type: 'post', count: counts.carousel_post, content_type: 'carousel' })
  }
  if (counts.feed_video_post > 0) {
    crosspostContents.push({ type: 'post', count: counts.feed_video_post, content_type: 'video' })
  }
  if (counts.short_form_video > 0) {
    videoContents.push({ type: 'post', count: counts.short_form_video })
  }
  if (counts.avatar_video > 0) {
    videoContents.push({ type: 'post', count: counts.avatar_video, content_type: 'avatar_video' })
  }
  if (counts.stories > 0) {
    instagramContents.push({ type: 'story', count: counts.stories })
  }
  if (counts.blog_post > 0) {
    seoContents.push({ type: 'blog_post', count: counts.blog_post })
  }
  if (counts.newsletter > 0) {
    emailContents.push({ type: 'newsletter', count: counts.newsletter })
  }

  return {
    crosspost_contents: crosspostContents,
    video_crosspost_contents: videoContents,
    instagram_contents: instagramContents,
    seo_contents: seoContents,
    email_contents: emailContents,
  }
}
